import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parseAsString, useQueryState, useQueryStates } from 'nuqs';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { TICKET_PRIORITIES, TICKET_STATUSES, platformTicketMessageInput, type PlatformPage, type TicketDetail, type TicketRow } from '@barbeiropro/contracts';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { FormModal } from '@/components/shared/form-modal';
import { PageHeader } from '@/components/shared/page-header';
import { LoadingState } from '@/components/shared/states';
import { useTableParams } from '@/components/shared/use-table-params';
import { Pill } from '@/features/finance/pill';
import { api, errorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';
import { dt, FilterSelect, useList, usePlatformAbility } from './shared';

export const TICKET_STATUS_LABEL: Record<string, string> = { open: 'Aberto', pending: 'Aguardando cliente', resolved: 'Resolvido', closed: 'Fechado' };
export const TICKET_PRIORITY_LABEL: Record<string, string> = { low: 'Baixa', normal: 'Normal', high: 'Alta', urgent: 'Urgente' };

function TicketModal({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { canTickets } = usePlatformAbility();
  const q = useQuery({ queryKey: ['platform', 'ticket', id], queryFn: () => api.get<TicketDetail>(`/platform/support/tickets/${id}`) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['platform'] });
  const patch = useMutation({
    mutationFn: (b: object) => api.patch(`/platform/support/tickets/${id}`, b),
    onSuccess: refresh,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const form = useForm<z.input<typeof platformTicketMessageInput>>({ resolver: zodResolver(platformTicketMessageInput) });
  const send = useMutation({
    mutationFn: (b: object) => api.post(`/platform/support/tickets/${id}/messages`, b),
    onSuccess: () => {
      form.reset({ body: '' });
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const t = q.data;
  return (
    <FormModal
      open
      onOpenChange={(o) => !o && onClose()}
      size="lg"
      title={t ? t.subject : 'Chamado'}
      description={t ? `${t.organizationName} · aberto em ${dt(t.createdAt)}` : undefined}
      footer={
        canTickets && t ? (
          <form id="reply" className="flex w-full flex-col gap-2 sm:flex-row" onSubmit={form.handleSubmit((v) => send.mutate(v))} noValidate>
            <Textarea aria-label="Resposta" rows={2} placeholder="Escreva a resposta" className="flex-1" {...form.register('body')} />
            <Button type="submit" disabled={send.isPending}>Responder</Button>
          </form>
        ) : (
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        )
      }
    >
      {q.isLoading || !t ? (
        <LoadingState />
      ) : (
        <div className="grid gap-4">
          <div className="flex flex-wrap gap-3">
            <Select value={t.status} onValueChange={(v) => patch.mutate({ status: v })} disabled={!canTickets}>
              <SelectTrigger size="sm" className="w-48" aria-label="Situação do chamado"><SelectValue /></SelectTrigger>
              <SelectContent>{TICKET_STATUSES.map((s) => <SelectItem key={s} value={s}>{TICKET_STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={t.priority} onValueChange={(v) => patch.mutate({ priority: v })} disabled={!canTickets}>
              <SelectTrigger size="sm" className="w-40" aria-label="Prioridade"><SelectValue /></SelectTrigger>
              <SelectContent>{TICKET_PRIORITIES.map((s) => <SelectItem key={s} value={s}>{TICKET_PRIORITY_LABEL[s]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <ol className="grid gap-3">
            {t.messages.map((m, i) => (
              <li key={i} className={cn('max-w-[90%] rounded-lg border p-3 text-sm', m.side === 'platform' ? 'ml-auto bg-accent' : 'bg-card')}>
                <p className="mb-1 text-xs text-muted-foreground">{m.author} · {dt(m.at)}</p>
                <p className="whitespace-pre-wrap break-words">{m.body}</p>
              </li>
            ))}
          </ol>
        </div>
      )}
    </FormModal>
  );
}

export function SupportPage() {
  const params = useTableParams({ sort: 'updatedAt', order: 'desc' });
  const [f, setF] = useQueryStates({ status: parseAsString, priority: parseAsString }, { history: 'replace' });
  const [open, setOpen] = useQueryState('chamado', parseAsString);
  const query = { ...params.query, status: f.status ?? undefined, priority: f.priority ?? undefined };
  const list = useList<PlatformPage<TicketRow>>('tickets', '/platform/support/tickets', query);
  const columns: DataColumn<TicketRow>[] = [
    { id: 'subject', header: 'Assunto', cell: ({ row }) => (<span><span className="font-medium">{row.original.subject}</span><span className="block text-xs text-muted-foreground">{row.original.organizationName}</span></span>) },
    { id: 'status', header: 'Situação', cell: ({ row }) => <Pill tone={row.original.status === 'open' ? 'warn' : 'neutral'}>{TICKET_STATUS_LABEL[row.original.status]}</Pill> },
    { id: 'priority', header: 'Prioridade', cell: ({ row }) => <Pill tone={row.original.priority === 'urgent' || row.original.priority === 'high' ? 'bad' : 'neutral'}>{TICKET_PRIORITY_LABEL[row.original.priority]}</Pill> },
    { id: 'messages', header: 'Mensagens', meta: { align: 'right' }, cell: ({ row }) => row.original.messagesCount },
    { id: 'updatedAt', header: 'Atualizado', meta: { sortKey: 'updatedAt' }, cell: ({ row }) => <span className="tabular whitespace-nowrap">{dt(row.original.updatedAt)}</span> },
  ];
  return (
    <div>
      <PageHeader title="Suporte" description="Chamados abertos pelas barbearias." />
      <DataTable
        columns={columns}
        data={list.data}
        params={params}
        isLoading={list.isLoading}
        error={list.error}
        onRetry={list.refetch}
        searchPlaceholder="Buscar por assunto ou barbearia"
        onRowClick={(r) => void setOpen(r.id)}
        empty={{ title: 'Nenhum chamado' }}
        filters={
          <>
            <FilterSelect label="Situação" value={f.status} onChange={(v) => { setF({ status: v }); params.set({ page: 1 }); }} options={TICKET_STATUSES.map((s) => ({ value: s, label: TICKET_STATUS_LABEL[s] }))} />
            <FilterSelect label="Prioridade" value={f.priority} onChange={(v) => { setF({ priority: v }); params.set({ page: 1 }); }} options={TICKET_PRIORITIES.map((s) => ({ value: s, label: TICKET_PRIORITY_LABEL[s] }))} />
          </>
        }
      />
      {open && <TicketModal id={open} onClose={() => void setOpen(null)} />}
    </div>
  );
}
